import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  parseAbsolute,
  startOfWeek,
  Time,
  toCalendarDateTime,
  today,
} from "@internationalized/date";
import { RangeValue } from "@react-types/shared";
import { format } from "date-fns";
import { DateTime } from "luxon";

import { padTwo } from "@/utils/string";

/** Anything more recent than this reads as "just now" rather than "N seconds ago". */
const JUST_NOW_THRESHOLD_SECONDS = 45;

export const fromNow = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";
  if (
    Math.abs(DateTime.now().diff(dt, "seconds").seconds) <
    JUST_NOW_THRESHOLD_SECONDS
  ) {
    return "just now";
  }

  return dt.toRelative() ?? "";
};

/**
 * Shows relative time up to 3 days ("1 day ago", "2 days ago", "3 days ago"),
 * then switches to absolute date in dd/MM/yyyy format.
 */
export const fromNowShort = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";
  const now = DateTime.now();
  const diffDays = Math.abs(now.diff(dt, "days").days);

  if (diffDays <= 3) {
    if (
      Math.abs(now.diff(dt, "seconds").seconds) < JUST_NOW_THRESHOLD_SECONDS
    ) {
      return "just now";
    }

    return dt.toRelative() ?? "";
  }

  return dt.toFormat("dd/MM/yyyy");
};

/**
 * Relative while it still reads as elapsed time ("2 minutes ago", "5 hours
 * ago"), then calendar language. The cut is at the day boundary rather than a
 * fixed number of hours: "23 hours ago" for something that happened yesterday
 * evening is arithmetic the reader has to do, "Yesterday" is not.
 */
export const fromNowThenDate = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  const now = DateTime.now();

  if (Math.abs(now.diff(dt, "seconds").seconds) < JUST_NOW_THRESHOLD_SECONDS) {
    return "Just now";
  }

  const days = now.startOf("day").diff(dt.startOf("day"), "days").days;

  // Future timestamps stay relative ("in 5 minutes") rather than being dated as
  // if they had already happened.
  if (days <= 0) return dt.toRelative() ?? "";
  if (days === 1) return "Yesterday";

  return dt.toFormat(dt.year === now.year ? "d LLL" : "d LLL yyyy");
};

/**
 * Single-token relative time ("now", "5m", "3h", "2d") for dense list columns
 * where "22 hours ago" would wrap and break row alignment. Falls back to a
 * short absolute date past a week.
 */
export const fromNowCompact = (date: string | Date) => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  const seconds = Math.abs(DateTime.now().diff(dt, "seconds").seconds);

  if (seconds < JUST_NOW_THRESHOLD_SECONDS) return "now";

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);

  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);

  if (days < 7) return `${days}d`;

  return dt.toFormat(dt.year === DateTime.now().year ? "d LLL" : "d LLL yy");
};

/**
 * The "last used" label on a picker row: elapsed time while it still reads as
 * such, then the day, then the date. Plain text beside the name, never a pill.
 */
export const formatLastUsed = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  const now = DateTime.now();
  const seconds = Math.abs(now.diff(dt, "seconds").seconds);

  if (seconds < JUST_NOW_THRESHOLD_SECONDS) return "Just now";

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) return `${minutes}m ago`;

  const days = now.startOf("day").diff(dt.startOf("day"), "days").days;

  if (days < 1) return `${Math.floor(minutes / 60)}h ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return dt.toFormat("ccc");

  return dt.toFormat(dt.year === now.year ? "d LLL" : "d LLL yyyy");
};

/** A day with its weekday, as a row's second line reads it: "Mon, Oct 5". */
export const formatWeekdayDate = (date: string): string =>
  DateTime.fromISO(date).toFormat("ccc, MMM d");

/** "Wed, Sep 30, 04:22 PM". */
export const formatWeekdayDateTime = (date: string): string =>
  `${formatWeekdayDate(date)}, ${formatTime(date)}`;

/** A `YYYY-MM` bucket as a person reads it: "Jul 2026". */
export const formatMonthLabel = (month: string): string => {
  const parsed = DateTime.fromFormat(month, "yyyy-MM");

  return parsed.isValid ? parsed.toFormat("LLL yyyy") : month;
};

export const formatDate = (date: string | Date): string => {
  const input =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  const now = DateTime.now();
  const todayStart = now.startOf("day");
  const yesterdayStart = now.minus({ days: 1 }).startOf("day");
  const twoDaysAgoStart = now.minus({ days: 2 }).startOf("day");

  if (input.hasSame(todayStart, "day")) {
    return "Today";
  }

  if (input.hasSame(yesterdayStart, "day")) {
    return "Yesterday";
  }

  if (input.hasSame(twoDaysAgoStart, "day")) {
    return "2 days ago";
  }

  if (input.hasSame(now, "year")) {
    return input.toFormat("MMM d");
  }

  return input.toFormat("MMM d, yyyy");
};

/**
 * Which calendar day a future timestamp lands on: "today", "tomorrow", a
 * weekday within the next week, else a short date.
 *
 * Compares calendar days rather than elapsed hours on purpose. 11pm tonight to
 * 10am tomorrow is only 11 hours, but rendering it as a bare "10:00 AM" reads
 * as today - which is exactly wrong for somebody who is off for the rest of
 * the day.
 */
export const formatFutureDay = (date: string | Date): string => {
  const input =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);
  const now = DateTime.now();

  if (input.hasSame(now, "day")) return "today";
  if (input.hasSame(now.plus({ days: 1 }), "day")) return "tomorrow";

  const daysAway = input.startOf("day").diff(now.startOf("day"), "days").days;

  if (daysAway > 1 && daysAway < 7) return input.toFormat("cccc");

  return input.toFormat("MMM d");
};

/**
 * An absolute calendar date - never "Today"/"Yesterday".
 *
 * `formatDate` is relative on purpose: it labels *conversation* time, where
 * "Yesterday" is what a reader wants. Machine-supplied values are the opposite
 * case - a booking window or a tool's date argument has to name the actual day,
 * because "Yesterday" there is a fact about when you opened the page rather
 * than about the thing being described.
 */
export const formatAbsoluteDate = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  return dt.toFormat("MMM d, yyyy");
};

/** `formatAbsoluteDate` with the time of day, for a precise instant. */
export const formatAbsoluteDateTime = (date: string | Date): string => {
  const day = formatAbsoluteDate(date);

  return day ? `${day} · ${formatTime(date)}` : "";
};

/** "Oct 11 · 2:32 PM", with the year only when it is not this one. */
export const formatShortDateTime = (date: string): string => {
  const dt = DateTime.fromISO(date);

  if (!dt.isValid) return "";

  const day = dt.toFormat(
    dt.hasSame(DateTime.now(), "year") ? "MMM d" : "MMM d, yyyy",
  );

  return `${day} · ${dt.toFormat("h:mm a")}`;
};

/**
 * To the second: "Aug 19, 2026 · 06:32:14 PM". Reports that pair a question
 * with its answer are read to settle "how long exactly", and a minute-rounded
 * stamp cannot answer that for a reply that landed 40 seconds later.
 */
export const formatAbsoluteTimestamp = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  return dt.toFormat("MMM d, yyyy · hh:mm:ss a");
};

/**
 * One window rather than two instants: "Jul 31, 2026 · 09:30 AM – 10:00 AM".
 * A booking has a start and an end, but a reader has one appointment - printing
 * the date twice to say so is the machine's shape leaking through. Falls back to
 * both instants in full when the window spans days.
 */
export const formatAbsoluteWindow = (
  start: string | Date,
  end: string | Date,
): string => {
  const from =
    typeof start === "string"
      ? DateTime.fromISO(start)
      : DateTime.fromJSDate(start);
  const to =
    typeof end === "string" ? DateTime.fromISO(end) : DateTime.fromJSDate(end);

  if (!from.hasSame(to, "day")) {
    return `${formatAbsoluteDateTime(start)} – ${formatAbsoluteDateTime(end)}`;
  }

  return `${formatAbsoluteDate(start)} · ${formatTime(start)} – ${formatTime(end)}`;
};

export const formatChartDate = (date: string | Date): string => {
  const input =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  return input.toFormat("MMM d");
};

export type Granularity =
  | "5min"
  | "2hour"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly";

export const getGranularity = (
  startDate: string | Date,
  endDate: string | Date,
): Granularity => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const durationMs = end.getTime() - start.getTime();
  const minutes = durationMs / (1000 * 60);
  const hours = minutes / 60;
  const days = hours / 24;
  const bucketDays = Math.ceil(days);

  if (bucketDays >= 365) return "monthly";
  if (bucketDays >= 28) return "weekly";
  if (days >= 2) return "daily";

  const isSameDay = start.toDateString() === end.toDateString();

  if (isSameDay || hours >= 24) return "hourly";
  if (minutes >= 60) return "2hour";

  return "5min";
};

export const formatChartTimestamp = (
  timestamp: string | Date,
  granularity: Granularity,
  timezone?: string | null,
): string => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp, { zone: timezone || undefined })
      : DateTime.fromJSDate(timestamp, { zone: timezone || undefined });

  if (!dt.isValid) return "";

  switch (granularity) {
    case "5min":
    case "2hour":
    case "hourly":
      return dt.toFormat("h:mm a");
    case "daily":
      return dt.toFormat("MMM d");
    case "weekly":
      return dt.toFormat("MMM d");
    case "monthly":
      return dt.toFormat("MMM yyyy");
  }
};

export const formatTime = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  // Unguarded, luxon renders unparseable input as the literal "Invalid
  // DateTime" straight into the UI. Every fromNow* helper here already returns
  // empty instead; the absolute formatters now match them.
  if (!dt.isValid) return "";

  return dt.toFormat("hh:mm a");
};

/** "4:30 PM" - `formatTime` without the leading zero, for schedule surfaces. */
export const formatShortTime = (date: string | Date): string => {
  const dt =
    typeof date === "string"
      ? DateTime.fromISO(date)
      : DateTime.fromJSDate(date);

  if (!dt.isValid) return "";

  return dt.toFormat("h:mm a");
};

/**
 * Compact same-day range: "9:00 – 11:00 AM", keeping the start meridiem only
 * when it differs ("11:30 AM – 1:30 PM").
 */
export const formatTimeRange = (
  start: string | Date,
  end: string | Date,
): string => {
  const s =
    typeof start === "string"
      ? DateTime.fromISO(start)
      : DateTime.fromJSDate(start);
  const e =
    typeof end === "string" ? DateTime.fromISO(end) : DateTime.fromJSDate(end);
  const sameMeridiem = s.toFormat("a") === e.toFormat("a");

  return `${s.toFormat(sameMeridiem ? "h:mm" : "h:mm a")} – ${e.toFormat("h:mm a")}`;
};

export const formatDateTime = (date: string | Date): string => {
  const formattedDate = formatDate(date);
  const formattedTime = formatTime(date);

  return `${formattedTime}, ${formattedDate}`;
};

export const formatDateTimeRange = (
  dateStart: string | Date,
  dateEnd: string | Date,
): string => {
  const dateStartAsDate =
    typeof dateStart === "string" ? new Date(dateStart) : dateStart;
  const dateEndAsDate =
    typeof dateEnd === "string" ? new Date(dateEnd) : dateEnd;
  const formattedDate = dateStartAsDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const formattedTimeRange =
    format(dateStartAsDate, "h:mm a") + " - " + format(dateEndAsDate, "h:mm a");

  return `${formattedDate}, ${formattedTimeRange}`;
};

export const isWithin24Hours = (timestamp: string | Date): boolean => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp)
      : DateTime.fromJSDate(timestamp);
  const hoursDiff = DateTime.now().diff(dt, "hours").hours;

  return hoursDiff < 24;
};

export const isWithin7Days = (timestamp: string | Date): boolean => {
  const dt =
    typeof timestamp === "string"
      ? DateTime.fromISO(timestamp)
      : DateTime.fromJSDate(timestamp);
  const daysDiff = DateTime.now().diff(dt, "days").days;

  return daysDiff < 7;
};

export const todayDate = () => {
  return DateTime.now().toFormat("yyyy-MM-dd");
};

export const oneWeekFromNowDate = () => {
  return DateTime.now().plus({ days: 7 }).toFormat("yyyy-MM-dd");
};

export const oneWeekBeforeDate = () => {
  return DateTime.now().minus({ days: 7 }).toFormat("yyyy-MM-dd");
};

export const isBefore = (date1: string, date2: string) => {
  return DateTime.fromISO(date1) < DateTime.fromISO(date2);
};

export const isAfter = (date1: string, date2: string) => {
  return DateTime.fromISO(date1) > DateTime.fromISO(date2);
};

export const isSameOrBefore = (date1: string | Date, date2: string | Date) => {
  const dt1 =
    typeof date1 === "string"
      ? DateTime.fromISO(date1)
      : DateTime.fromJSDate(date1);
  const dt2 =
    typeof date2 === "string"
      ? DateTime.fromISO(date2)
      : DateTime.fromJSDate(date2);

  return dt1 <= dt2;
};

export const USER_TIMEZONE = DateTime.local().zoneName ?? "UTC";

/** On a week's first day, the hour from which the day has enough of its own to show. */
const WEEK_START_OWN_DATA_HOUR = 18;

/**
 * Returns the default date range for reports: the start of this week to today.
 * A week's first day has next to nothing in it yet, so until the evening the
 * default is the whole of last week.
 */
export const getDefaultDateRange = (): RangeValue<CalendarDateTime> => {
  const todayDate = today(getLocalTimeZone());
  const weekStart = startOfWeek(todayDate, "en-US");
  const showsLastWeek =
    todayDate.compare(weekStart) === 0 &&
    new Date().getHours() < WEEK_START_OWN_DATA_HOUR;
  const start = showsLastWeek ? weekStart.subtract({ weeks: 1 }) : weekStart;
  const end = showsLastWeek ? weekStart.subtract({ days: 1 }) : todayDate;

  return {
    start: toCalendarDateTime(start),
    end: toCalendarDateTime(end, new Time(23, 59, 59)),
  };
};

/**
 * Returns the nearest time option from a list of time strings (format: "hh:mm AM/PM")
 * based on the current time
 */
export const getNearestTimeOption = (timeOptions: string[]): string => {
  const now = DateTime.now();
  const currentMinutes = now.hour * 60 + now.minute;

  let nearestTime = timeOptions[0];
  let minDiff = Infinity;

  for (const time of timeOptions) {
    const [timeStr, period] = time.split(" ");
    const [hourStr, minuteStr] = timeStr.split(":");
    let hour = parseInt(hourStr);
    const minute = parseInt(minuteStr);

    if (period === "PM" && hour !== 12) hour += 12;
    if (period === "AM" && hour === 12) hour = 0;

    const optionMinutes = hour * 60 + minute;
    const diff = Math.abs(optionMinutes - currentMinutes);

    if (diff < minDiff) {
      minDiff = diff;
      nearestTime = time;
    }
  }

  return nearestTime;
};

/**
 * Generate time options with specified interval (default: 15 minutes)
 * Returns array of time strings in "hh:mm a" format (e.g., "09:00 AM")
 */
export const generateTimeOptions = (interval: number = 15): string[] => {
  const times: string[] = [];
  let current = DateTime.now().startOf("day");
  const end = DateTime.now().endOf("day");

  while (current < end) {
    times.push(current.toFormat("hh:mm a"));
    current = current.plus({ minutes: interval });
  }

  return times;
};

/**
 * Format hour/minute to time string (e.g., "09:00 AM")
 */
export const formatTimeValue = (hour: number, minute: number): string => {
  return DateTime.now().set({ hour, minute }).toFormat("hh:mm a");
};

/**
 * Format hour/minute to 24-hour wire format string (e.g., "09:05")
 */
export const format24HourTime = (hour: number, minute: number): string =>
  `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;

/**
 * Parse 24-hour wire format string (e.g., "09:05") to hour/minute.
 * Defaults to 0 for any malformed segment.
 */
export const parseTime24Hour = (
  value: string,
): { hour: number; minute: number } => {
  const [h, m] = value.split(":").map(Number);

  return { hour: h ?? 0, minute: m ?? 0 };
};

/**
 * Parse time string (e.g., "09:00 AM") to hour/minute in 24-hour format
 */
export const parseTimeString = (
  timeStr: string,
): { hour: number; minute: number } | null => {
  const [time, period] = timeStr.split(" ");

  if (!time || !period) return null;

  const [hourStr, minuteStr] = time.split(":");
  let hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);

  if (isNaN(hour) || isNaN(minute)) return null;

  // Convert to 24-hour format
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;

  return { hour, minute };
};

/**
 * Convert ISO string to CalendarDateTime
 */
export const isoToCalendarDateTime = (
  isoString: string | null | undefined,
): CalendarDateTime | null => {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) return null;

    return new CalendarDateTime(
      date.getFullYear(),
      date.getMonth() + 1, // CalendarDateTime months are 1-indexed
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
    );
  } catch {
    return null;
  }
};

export const isoToCalendarDate = (
  isoString: string | null | undefined,
): CalendarDate | null => {
  if (!isoString) return null;

  try {
    const date = new Date(isoString);

    if (isNaN(date.getTime())) return null;

    return new CalendarDate(
      date.getFullYear(),
      date.getMonth() + 1, // CalendarDateTime months are 1-indexed
      date.getDate(),
    );
  } catch {
    return null;
  }
};

/**
 * Convert local time string ("hh:mm a") to UTC time string ("HH:mm")
 */
export const localTimeToUtc = (timeStr: string): string => {
  const parsed = parseTimeString(timeStr);

  if (!parsed) return timeStr;

  const local = DateTime.now().set({
    hour: parsed.hour,
    minute: parsed.minute,
    second: 0,
    millisecond: 0,
  });

  const utc = local.toUTC();

  return utc.toFormat("HH:mm");
};

/**
 * Convert UTC time string ("HH:mm") to local time string ("hh:mm a")
 */
/**
 * Format a duration in minutes to a human-readable string.
 * e.g. 12 → "12m", 145 → "2h 25m", 1500 → "1d 1h". `null` when nothing was timed.
 */
export function formatDurationMinutes(minutes: number): string;
export function formatDurationMinutes(minutes: number | null): string | null;
export function formatDurationMinutes(minutes: number | null): string | null {
  if (minutes === null) return null;
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours < 24) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
}

/**
 * Human-readable elapsed time between two timestamps, seconds-aware.
 * e.g. 21s, 2m 4s, 12m 8s, 1h 3m. Unlike `formatDurationMinutes` this doesn't
 * collapse anything under a minute to "<1m", which matters for short jobs.
 */
export const formatElapsed = (
  start: string | Date,
  end: string | Date,
): string => {
  const from =
    typeof start === "string"
      ? DateTime.fromISO(start)
      : DateTime.fromJSDate(start);
  const to =
    typeof end === "string" ? DateTime.fromISO(end) : DateTime.fromJSDate(end);

  if (!from.isValid || !to.isValid) return "-";

  const seconds = Math.max(0, Math.round(to.diff(from, "seconds").seconds));

  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

/** A UTC "HH:mm" on the browser's clock, as "hh:mm a" or the given `format`. */
export const utcToLocalTime = (
  utcTimeStr: string,
  format = "hh:mm a",
): string => {
  const [hourStr, minuteStr] = utcTimeStr.split(":");
  const hour = parseInt(hourStr);
  const minute = parseInt(minuteStr);

  if (isNaN(hour) || isNaN(minute)) return utcTimeStr;

  const utc = DateTime.utc().set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  });

  const local = utc.toLocal();

  return local.toFormat(format);
};

/**
 * Convert a picked CalendarDateTime to an instant, reading the wall-clock in
 * `timeZone` rather than the browser's.
 *
 * For reports only. The picked value is a wall-clock with no zone attached, so
 * "1 Aug 00:00" has to be anchored somewhere before it can bound a query. The
 * browser is the wrong anchor: the backend buckets every trend with `AT TIME
 * ZONE` the organization's zone, so anchoring the range anywhere else leaves
 * the first and last buckets clipped and the totals disagreeing with the sum of
 * the series. Whoever is looking, a clinic's day starts when the clinic opens.
 *
 * Everything else - snoozes, follow-ups, appointment forms - keeps using
 * {@link calendarDateTimeToIso}, where the user's own clock is the right one.
 */
export const calendarDateTimeToZonedIso = (
  dateTime: CalendarDateTime | null,
  timeZone?: string | null,
): string | null => {
  if (!dateTime) return null;

  try {
    return dateTime.toDate(timeZone || getLocalTimeZone()).toISOString();
  } catch {
    // An unknown or malformed IANA name on the organization. Falling back to
    // the browser is the old behaviour - worse, but not a broken page.
    return dateTime.toDate(getLocalTimeZone()).toISOString();
  }
};

/** An instant as the wall clock reads it in `timeZone`; the inverse of {@link calendarDateTimeToZonedIso}. */
export const zonedIsoToCalendarDateTime = (
  iso: string,
  timeZone?: string | null,
): CalendarDateTime | null => {
  try {
    return toCalendarDateTime(
      parseAbsolute(iso, timeZone || getLocalTimeZone()),
    );
  } catch {
    return null;
  }
};

/**
 * Convert CalendarDateTime to ISO string
 */
export const calendarDateTimeToIso = (
  dateTime: CalendarDateTime | null,
): string | null => {
  if (!dateTime) return null;

  const date = new Date(
    dateTime.year,
    dateTime.month - 1, // JS Date months are 0-indexed
    dateTime.day,
    dateTime.hour,
    dateTime.minute,
    dateTime.second || 0,
  );

  return date.toISOString();
};

/**
 * Structural shape for any @internationalized/date value. Used to bridge the
 * version skew between our pinned @internationalized/date and the copy nested
 * under react-stately/react-aria, whose CalendarDateTime is structurally
 * distinct under TypeScript.
 */
export interface DateValueLike {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}

/**
 * Coerce a DateValue-shaped object (from any @internationalized/date copy)
 * into our local CalendarDateTime instance.
 */
export const toLocalCalendarDateTime = (
  date: DateValueLike | null,
): CalendarDateTime | null => {
  if (!date) return null;

  return new CalendarDateTime(
    date.year,
    date.month,
    date.day,
    date.hour ?? 0,
    date.minute ?? 0,
    date.second ?? 0,
  );
};

export const formatCalendarDateTime = (
  dateTime: CalendarDateTime,
  format?: string,
): string => {
  return DateTime.fromObject({
    year: dateTime.year,
    month: dateTime.month,
    day: dateTime.day,
    hour: dateTime.hour,
    minute: dateTime.minute,
  }).toFormat(format ?? "MMM dd, yyyy hh:mm a");
};

export const formatCalendarTime = (dateTime: CalendarDateTime): string => {
  return DateTime.now()
    .set({ hour: dateTime.hour, minute: dateTime.minute })
    .toFormat("hh:mm a");
};

const isFullDayCalendarRange = (
  start: CalendarDateTime,
  end: CalendarDateTime,
): boolean =>
  start.hour === 0 &&
  start.minute === 0 &&
  end.hour === 23 &&
  (end.minute === 59 || end.minute === 0);

// Format CalendarDateTime range for display.
// - Drops the year segment when start and end share the same year.
// - Drops the time segment when the range spans a full day (00:00 → 23:59).
export const formatCalendarDateTimeRange = (
  start: CalendarDateTime,
  end: CalendarDateTime,
): string => {
  try {
    const startLuxon = DateTime.fromObject({
      year: start.year,
      month: start.month,
      day: start.day,
      hour: start.hour,
      minute: start.minute,
    });
    const endLuxon = DateTime.fromObject({
      year: end.year,
      month: end.month,
      day: end.day,
      hour: end.hour,
      minute: end.minute,
    });

    const sameYear = start.year === end.year;
    const fullDay = isFullDayCalendarRange(start, end);

    const datePart = sameYear ? "MMM dd" : "MMM dd, yyyy";
    const format = fullDay ? datePart : `${datePart} hh:mm a`;

    return `${startLuxon.toFormat(format)} - ${endLuxon.toFormat(format)}`;
  } catch (e) {
    console.error("Error formatting date range:", start, end, e);

    return "Invalid date range";
  }
};

/**
 * Get current CalendarDateTime (now)
 */
export const getCurrentCalendarDateTime = (): CalendarDateTime => {
  const now = DateTime.now();

  return new CalendarDateTime(
    now.year,
    now.month,
    now.day,
    now.hour,
    now.minute,
    now.second,
  );
};

/** A reminder `days` out, at 10:00 local - mid-morning, when a desk is staffed. */
export const remindInDays = (days: number): string =>
  DateTime.now()
    .plus({ days })
    .set({ hour: 10, minute: 0, second: 0, millisecond: 0 })
    .toJSDate()
    .toISOString();

/**
 * Default "they asked for time until" date: a week out - long enough to be a
 * real gap, short enough to still be warm.
 */
export const defaultWaitingOnUserTill = (): string => remindInDays(7);

/** Default recall for a patient who was all done: six months out. */
export const defaultRecallAt = (): string => remindInDays(180);

/**
 * Compact elapsed-time label for an overdue moment, e.g. "45m", "3h 20m", "18d 5h".
 * Days and hours derive from the same elapsed minutes so the parts stay consistent.
 */
export const formatOverdueDuration = (overdueMinutes: number): string => {
  if (overdueMinutes < 60) return `${overdueMinutes}m`;

  const totalHours = Math.floor(overdueMinutes / 60);

  if (totalHours < 24) {
    const minutes = overdueMinutes % 60;

    return minutes > 0 ? `${totalHours}h ${minutes}m` : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
};

export const formatFollowupDate = (dueDate: string): string => {
  const dt = DateTime.fromISO(dueDate).toLocal();
  const now = DateTime.now();

  const diffDays = Math.round(
    dt.startOf("day").diff(now.startOf("day"), "days").days,
  );

  const time = dt.toFormat("h:mm a");

  if (diffDays < 0) {
    const overdueMinutes = Math.floor(now.diff(dt, "minutes").minutes);

    return formatOverdueDuration(overdueMinutes);
  }

  if (diffDays === 0) {
    const diffMinutes = Math.floor(dt.diff(now, "minutes").minutes);

    if (diffMinutes <= 0) return "Due now";

    if (diffMinutes < 60) {
      return `In ${diffMinutes}m`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return minutes > 0 ? `In ${hours}h ${minutes}m` : `In ${hours}h`;
  }

  if (diffDays === 1) return `Tomorrow, ${time}`;
  if (diffDays <= 7) return `In ${diffDays}d, ${time}`;

  return dt.toFormat("MMM dd, h:mm a");
};

export const isOverdue = (dueDate: string): boolean => {
  return DateTime.fromISO(dueDate).toLocal() < DateTime.now();
};

export const isDueToday = (dueDate: string): boolean => {
  return DateTime.fromISO(dueDate)
    .toLocal()
    .startOf("day")
    .equals(DateTime.now().startOf("day"));
};

/**
 * Duration of a single agent step. Unlike `formatElapsed` this keeps a decimal
 * under ten seconds - most tool calls finish in a few hundred milliseconds and
 * would otherwise all render "0s".
 */
export const formatStepDuration = (ms: number): string => {
  const safe = Math.max(0, ms);

  if (safe < 10_000) return `${(safe / 1000).toFixed(1)}s`;
  if (safe < 60_000) return `${Math.round(safe / 1000)}s`;

  const totalSeconds = Math.round(safe / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

/** Seconds → `m:ss`, for a recording or an audio player's time. */
export const formatDuration = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${padTwo(Math.floor(seconds % 60))}`;

/**
 * ISO for one edge of a whole-day range: the start at 00:00, the end at 23:59, so
 * "1st to 1st" means all of that day. A day-granularity picker emits a bare date
 * (midnight), and its `placeholderValue` is ignored on selection - so the edge
 * times have to be forced here rather than hoped for.
 */
export const dayEdgeToIso = (
  value: CalendarDateTime | null,
  edge: "start" | "end",
): string | null =>
  calendarDateTimeToIso(
    value?.set(
      edge === "end" ? { hour: 23, minute: 59 } : { hour: 0, minute: 0 },
    ) ?? null,
  );

/** An open-ended ISO range as persisted by the filter/segment rule editors. */
export interface IsoDateRange {
  start: string | null;
  end: string | null;
}

/**
 * ICU disagrees with itself across versions on these: some builds resolve the
 * browser timezone to the legacy spelling, newer ones to the modern one. Both
 * are accepted as input everywhere, so the app standardises on the modern name
 * - the one the timezone list and the currency/region map are keyed by. Without
 * this a Kathmandu browser reports "Asia/Katmandu", matches nothing, and
 * silently falls back to USD/US with an empty timezone select.
 */
const TIMEZONE_ALIASES: Record<string, string> = {
  "Asia/Katmandu": "Asia/Kathmandu",
  "Asia/Calcutta": "Asia/Kolkata",
};

export const normalizeTimezone = (timezone: string): string =>
  TIMEZONE_ALIASES[timezone] ?? timezone;

export const getDetectedTimezone = (): string =>
  normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);

/** Calendar days from an ISO date or timestamp to today, never negative; 0 when it cannot be read. */
export const daysSince = (date: string): number => {
  const days = DateTime.now()
    .startOf("day")
    .diff(DateTime.fromISO(date).startOf("day"), "days").days;

  return Number.isFinite(days) ? Math.max(Math.round(days), 0) : 0;
};

export const minutesSince = (date: string, until?: string): number => {
  const dt = DateTime.fromISO(date);
  const end = until ? DateTime.fromISO(until) : DateTime.now();

  if (!dt.isValid || !end.isValid) return 0;

  return Math.max(0, Math.floor(end.diff(dt, "minutes").minutes));
};

/** Today in `timeZone` (the browser's when unset), as the ISO edges a report range takes. */
export const zonedTodayIsoRange = (
  timeZone?: string | null,
): { startDate: string; endDate: string } => {
  const now = DateTime.now().setZone(timeZone || USER_TIMEZONE);

  return {
    startDate: now.startOf("day").toUTC().toISO()!,
    endDate: now.endOf("day").toUTC().toISO()!,
  };
};

/** A moment on a workspace's clock; the browser's when `timezone` is unset. */
export const inTimezone = (iso: string, timezone?: string | null) =>
  DateTime.fromISO(iso, { zone: timezone || undefined });

/** The day an instant falls on, on the given clock, as `YYYY-MM-DD`: a ledger date then prints as the same day wherever it is read. */
export const dayInTimezone = (
  iso: string,
  timezone?: string | null,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid ? date.toFormat("yyyy-MM-dd") : iso;
};

/** "18 Sep", or "12 Mar 2026" with `withYear`, on the given clock. */
export const formatDayMonth = (
  iso: string,
  timezone?: string | null,
  withYear = false,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid ? date.toFormat(withYear ? "d MMM yyyy" : "d MMM") : "";
};

/** "9 Oct, 12:06", on the given clock. */
export const formatDayMonthTime = (
  iso: string,
  timezone?: string | null,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid ? date.toFormat("d MMM, HH:mm") : "";
};

/** "Fri 18 Sep", or "Fri 18 Sep 2026" with `withYear`, on the given clock. */
export const formatWeekdayDayMonth = (
  iso: string,
  timezone?: string | null,
  withYear = false,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid
    ? date.toFormat(withYear ? "ccc d MMM yyyy" : "ccc d MMM")
    : "";
};

/** "10:42", on the given clock. */
export const formatClockTime = (
  iso: string,
  timezone?: string | null,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid ? date.toFormat("HH:mm") : "";
};

/** "Thu 8 Oct, 12:00", on the given clock. */
export const formatWeekdayDayMonthTime = (
  iso: string,
  timezone?: string | null,
): string => {
  const date = inTimezone(iso, timezone);

  return date.isValid ? date.toFormat("ccc d MMM, HH:mm") : "";
};

/** "10 Sep to 9 Oct 2026", the start's year said only when it differs. */
export const formatDayRange = (
  startDate?: string,
  endDate?: string,
  timezone?: string | null,
): string => {
  if (!startDate || !endDate) return "";
  const start = inTimezone(startDate, timezone);
  const end = inTimezone(endDate, timezone);

  if (!start.isValid || !end.isValid) return "";

  return `${start.toFormat(start.year === end.year ? "d MMM" : "d MMM yyyy")} to ${end.toFormat("d MMM yyyy")}`;
};

/** An "HH:mm" time without minutes on the hour: "10 AM", "1:30 PM". */
export const formatTimeOfDay = (time: string): string => {
  const at = DateTime.fromFormat(time, "HH:mm");

  return at.toFormat(at.minute === 0 ? "h a" : "h:mm a");
};

export const formatTimeSpan = (start: string, end: string): string =>
  `${formatTimeOfDay(start)} to ${formatTimeOfDay(end)}`;

/** "Sat 17 Oct", or "Mon 19 to Wed 21 Oct" for a run of days. */
export const formatDateSpan = (from: string, to: string): string => {
  const last = DateTime.fromISO(to);

  if (from === to) return last.toFormat("ccc d LLL");

  const first = DateTime.fromISO(from);
  const firstFormat = first.hasSame(last, "month") ? "ccc d" : "ccc d LLL";

  return `${first.toFormat(firstFormat)} to ${last.toFormat("ccc d LLL")}`;
};

/** Both ends included, as "YYYY-MM-DD". */
export const datesBetween = (from: string, to: string): string[] => {
  const last = DateTime.fromISO(to);
  const dates: string[] = [];

  for (
    let day = DateTime.fromISO(from);
    day <= last;
    day = day.plus({ days: 1 })
  ) {
    dates.push(day.toFormat("yyyy-MM-dd"));
  }

  return dates;
};
